import { useEffect, useState } from 'react';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Phone,
  ShoppingCart,
  ArrowRight,
  CheckCircle,
  Layers,
  Hammer,
  Building2,
  Clock,
  Users,
  Award,
  Shield,
  MessageCircle,
  Star,
  ChevronDown,
  HelpCircle,
  Loader2,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useProducts } from '../hooks/useProducts';
import { useCart } from '../context/CartContext';

const WHATSAPP_NUMBER = '551151250926';
const WHATSAPP_MESSAGE = 'Olá, vim pelo site de vocês. Quero fazer um orçamento para....';
const NUVEMSHOP_URL = 'https://ematech6.lojavirtualnuvem.com.br/';

// Animation variants - Premium
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { 
    opacity: 1, 
    y: 0, 
    transition: { 
      duration: 0.6, 
      ease: [0.4, 0, 0.2, 1] 
    } 
  },
};

const fadeInScale = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: { 
    opacity: 1, 
    scale: 1, 
    transition: { 
      duration: 0.5, 
      ease: [0.4, 0, 0.2, 1] 
    } 
  },
};

const slideInLeft = {
  hidden: { opacity: 0, x: -30 },
  visible: { 
    opacity: 1, 
    x: 0, 
    transition: { 
      duration: 0.6, 
      ease: [0.4, 0, 0.2, 1] 
    } 
  },
};

const slideInRight = {
  hidden: { opacity: 0, x: 30 },
  visible: { 
    opacity: 1, 
    x: 0, 
    transition: { 
      duration: 0.6, 
      ease: [0.4, 0, 0.2, 1] 
    } 
  },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.12, delayChildren: 0.1 },
  },
};

// Animated Section Component
const AnimatedSection = ({ children, className = '', id }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  
  return (
    <motion.section
      ref={ref}
      id={id}
      initial="hidden"
      animate={isInView ? 'visible' : 'hidden'}
      variants={staggerContainer}
      className={className}
    >
      {children}
    </motion.section>
  );
};

// Hero Section
const HeroSection = () => {
  const handleWhatsAppClick = () => {
    if (window.dataLayer) {
      window.dataLayer.push({ event: 'whatsapp_click', event_label: 'hero_section' });
    }
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(WHATSAPP_MESSAGE)}`, '_blank');
  };

  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      style={{
        backgroundImage: `url(${process.env.PUBLIC_URL}/images/hero/hero-bg.jpg)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed'
      }}
    >
      {/* Overlay with gradient - mais sólido */}
      <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-slate-900/85 to-slate-900/75" />
      
      {/* Content */}
      <div className="relative z-10 container mx-auto px-4 md:px-6 max-w-7xl pt-20">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="max-w-4xl"
        >
          {/* Premium Badge */}
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500/15 border border-amber-500/30 text-amber-400 text-sm font-semibold rounded-full mb-8 backdrop-blur-sm"
          >
            <span className="w-2 h-2 bg-amber-400 rounded-full animate-pulse"></span>
            +20 Anos de Experiência • Fabricação Própria
          </motion.div>
          
          {/* Headline com hierarquia premium */}
          <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-extrabold text-white uppercase tracking-tighter leading-[0.95] mb-8">
            Soluções Técnicas em
            <br />
            <span className="text-amber-400">Revestimento</span>
            <br />
            <span className="text-slate-400 text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold">e Reforço Estrutural</span>
          </h1>
          
          {/* Subheadline refinada */}
          <p className="text-base md:text-lg lg:text-xl text-slate-300 max-w-2xl mb-10 leading-relaxed font-light tracking-wide">
            Pinturas Industriais, Revestimentos, Reforço com Epóxi e Estruturação de Pontes e Prédios. 
            Entregamos <span className="text-white font-medium">segurança e qualidade</span> com precisão, agilidade e durabilidade comprovada.
            <br /><br />
            <span className="text-amber-400 font-medium">Atendimento para indústrias, obras civis, condomínios e galpões.</span>
          </p>
          
          {/* CTAs com design limpo */}
          <div className="flex flex-col sm:flex-row gap-4">
            <Button
              data-testid="hero-whatsapp-btn"
              onClick={handleWhatsAppClick}
              className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold px-8 py-6 text-base rounded-lg shadow-lg hover:shadow-xl flex items-center justify-center gap-3 uppercase tracking-widest transition-all duration-200 hover:-translate-y-0.5 active:translate-y-0"
            >
              <Phone className="w-5 h-5" />
              Falar no WhatsApp
            </Button>
            
            <a href="#produtos">
              <Button
                data-testid="hero-products-btn"
                variant="outline"
                className="w-full sm:w-auto bg-white/5 backdrop-blur-sm border-white/25 text-white hover:bg-white/10 hover:border-white/40 font-semibold px-8 py-6 text-base rounded-lg flex items-center justify-center gap-3 transition-all duration-200"
              >
                <ShoppingCart className="w-5 h-5" />
                Ver Produtos
              </Button>
            </a>
          </div>
          
          {/* Trust indicators */}
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1 }}
            className="mt-12 pt-8 border-t border-white/10 flex flex-wrap items-center gap-6 text-sm text-slate-400"
          >
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-amber-500" />
              <span>Atendimento Nacional</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-amber-500" />
              <span>Suporte Técnico</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-amber-500" />
              <span>Fabricação Própria</span>
            </div>
          </motion.div>
        </motion.div>
      </div>
      
      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
      >
        <div className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-1">
          <motion.div
            animate={{ y: [0, 12, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            className="w-1.5 h-3 bg-white/60 rounded-full"
          />
        </div>
      </motion.div>
    </section>
  );
};

// Authority Section
const AuthoritySection = () => {
  const stats = [
    { icon: Clock, value: '20+', label: 'Anos de', sublabel: 'Experiência' },
    { icon: Users, value: '500+', label: 'Clientes', sublabel: 'Atendidos' },
    { icon: Award, value: '100%', label: 'Fabricação', sublabel: 'Própria' },
    { icon: Shield, value: 'Brasil', label: 'Atendimento', sublabel: 'Nacional' },
  ];

  return (
    <AnimatedSection className="py-16 md:py-20 bg-slate-900 relative overflow-hidden">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }} />
      </div>
      
      <div className="container mx-auto px-4 md:px-6 max-w-7xl relative z-10">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {stats.map((stat, index) => (
            <motion.div
              key={stat.label}
              variants={fadeInUp}
              data-testid={`stat-${index}`}
              className="text-center p-6 md:p-8 bg-white/[0.06] backdrop-blur-sm rounded-xl border border-white/10 hover:bg-white/[0.08] transition-all duration-200"
            >
              <div className="w-12 h-12 bg-amber-500/20 rounded-lg flex items-center justify-center mx-auto mb-4">
                <stat.icon className="w-6 h-6 text-amber-400" />
              </div>
              <div className="font-heading text-3xl md:text-4xl lg:text-5xl font-extrabold text-white mb-1 tracking-tight">
                {stat.value}
              </div>
              <div className="text-slate-400 text-xs md:text-sm uppercase tracking-widest leading-tight">
                {stat.label}
                <br />
                <span className="text-slate-500">{stat.sublabel}</span>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </AnimatedSection>
  );
};

// Services Section
const ServicesSection = () => {
  const handleWhatsAppClick = (service) => {
    if (window.dataLayer) {
      window.dataLayer.push({ event: 'whatsapp_click', event_label: `service_${service}` });
    }
    const message = `Olá, vim pelo site de vocês. Quero fazer um orçamento para ${service}.`;
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`, '_blank');
  };

  const services = [
    {
      id: 'pintura',
      icon: Layers,
      title: 'Pintura e Revestimento Industrial',
      description: 'Cobertura resistente para pisos de alto tráfego, garagens, galpões, shoppings, quadras, frigoríficos e hangares.',
      features: ['Tintas PU e epóxi de fabricação própria', 'Resistência química e abrasiva', 'Aplicação rápida'],
      image: '/images/services/pintura-industrial.jpeg',
    },
    {
      id: 'reforco',
      icon: Hammer,
      title: 'Reforço Estrutural com Epóxi',
      description: 'Recuperação de estruturas comprometidas, sem demolição. Solução técnica e econômica.',
      features: ['Injeção epóxi para fissuras', 'Reforço de colunas e vigas', 'Impermeabilização'],
      image: '/images/services/reforco-estrutural.jpeg',
    },
    {
      id: 'estruturacao',
      icon: Building2,
      title: 'Estruturação de Pontes e Viadutos',
      description: 'Soluções técnicas para pontos críticos da engenharia pesada com materiais de alta performance.',
      features: ['Lábios poliméricos', 'Vedação e união de concretos', 'Proteção contra erosão'],
      image: '/images/services/estruturacao-pontes.jpeg',
    },
  ];

  return (
    <AnimatedSection id="servicos" className="py-20 md:py-32 bg-slate-50">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <motion.div variants={fadeInUp} className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-amber-600 text-xs font-bold uppercase tracking-[0.2em] mb-4 block">
            Nossos Serviços
          </span>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-extrabold text-slate-900 uppercase tracking-tight mb-6 leading-tight">
            Soluções Completas<br />
            <span className="text-slate-500">para sua Obra</span>
          </h2>
          <p className="text-slate-600 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
            Da fabricação à aplicação, oferecemos serviços técnicos especializados com materiais de alta qualidade.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <motion.div
              key={service.id}
              variants={fadeInUp}
              data-testid={`service-card-${service.id}`}
              className="bg-white rounded-xl overflow-hidden border border-slate-200/60 shadow-sm hover:shadow-md transition-all duration-200 service-card group flex flex-col"
            >
              <div className="relative h-48 overflow-hidden">
                <img
                  src={service.image}
                  alt={service.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 via-slate-900/20 to-transparent" />
                <div className="absolute bottom-4 left-4">
                  <div className="w-12 h-12 bg-amber-500 rounded-lg flex items-center justify-center">
                    <service.icon className="w-6 h-6 text-slate-900" />
                  </div>
                </div>
              </div>
              
              <div className="p-6 flex flex-col flex-grow">
                <h3 className="font-heading text-xl font-bold text-slate-900 mb-3">
                  {service.title}
                </h3>
                <p className="text-slate-600 text-sm mb-4 leading-relaxed">
                  {service.description}
                </p>
                <ul className="space-y-2 mb-6">
                  {service.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-slate-600">
                      <CheckCircle className="w-4 h-4 text-amber-500 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <div className="mt-auto">
                  <Button
                    data-testid={`service-cta-${service.id}`}
                    onClick={() => handleWhatsAppClick(service.title)}
                    className="w-full bg-slate-900 hover:bg-slate-800 text-white font-semibold py-3 rounded-lg flex items-center justify-center gap-2 transition-colors duration-200"
                  >
                    <MessageCircle className="w-4 h-4" />
                    Solicitar Orçamento
                  </Button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </AnimatedSection>
  );
};

// Products Section
const ProductsSection = ({ addToCart, products, loading }) => {
  const [addedProduct, setAddedProduct] = useState(null);

  const handleBuyClick = (product) => {
    if (window.dataLayer) {
      window.dataLayer.push({ event: 'add_to_cart', event_label: product.name });
    }
    
    // Adicionar ao carrinho
    addToCart({
      id: product.id,
      name: product.name,
      weight: product.weight,
      price: product.price,
      image: product.image,
      variantId: product.variantId || null,
      checkoutUrl: product.checkoutUrl
    });
    
    // Feedback visual
    setAddedProduct(product.id);
    setTimeout(() => setAddedProduct(null), 2000);
  };

  const handleWhatsAppClick = (product) => {
    if (window.dataLayer) {
      window.dataLayer.push({ event: 'whatsapp_click', event_label: `product_${product.name}` });
    }
    const message = `Olá, vim pelo site de vocês. Tenho interesse no produto: ${product.name} ${product.weight || ''}`;
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`, '_blank');
  };

  const displayedProducts = products.slice(0, 6);

  return (
    <AnimatedSection id="produtos" className="py-20 md:py-32 bg-white">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <motion.div variants={fadeInUp} className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-amber-600 text-xs font-bold uppercase tracking-[0.2em] mb-4 block">
            Loja Virtual
          </span>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-extrabold text-slate-900 uppercase tracking-tight mb-6 leading-tight">
            Nossos Produtos
          </h2>
          <p className="text-slate-600 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
            Resinas, tintas e revestimentos de <span className="text-slate-900 font-semibold">fabricação própria</span>. Qualidade industrial com preços competitivos.
          </p>
        </motion.div>

        {/* Loading State */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-16">
            <Loader2 className="w-10 h-10 text-amber-500 animate-spin mb-4" />
            <p className="text-slate-600">Carregando produtos...</p>
          </div>
        )}

        {!loading && displayedProducts.length > 0 && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {displayedProducts.map((product) => (
            <motion.div
              key={product.id}
              variants={fadeInUp}
              data-testid={`product-card-${product.id}`}
              className="bg-white rounded-xl overflow-hidden border border-slate-200/60 shadow-sm hover:shadow-md transition-all duration-200 group product-card flex flex-col"
            >
              {/* Product Image */}
              <div className="relative h-52 overflow-hidden bg-slate-100">
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-full object-cover product-image transition-transform duration-500 group-hover:scale-105"
                />
                {product.discount > 0 && (
                  <span className="absolute top-3 left-3 bg-red-500 text-white text-xs font-bold px-2.5 py-1 rounded-md">
                    {product.discount}% OFF
                  </span>
                )}
              </div>
              
              {/* Product Info */}
              <div className="p-5 flex flex-col flex-grow">
                <h3 className="font-heading text-lg font-bold text-slate-900 mb-1">
                  {product.name}
                </h3>
                <p className="text-slate-500 text-sm mb-2">{product.weight}</p>
                <p className="text-slate-600 text-sm mb-4 line-clamp-2 leading-relaxed">
                  {product.description}
                </p>
                
                {/* Price */}
                <div className="mb-4">
                  <div className="flex items-baseline gap-2">
                    <span className="text-2xl font-bold text-slate-900">
                      R$ {product.price.toFixed(2).replace('.', ',')}
                    </span>
                    {product.originalPrice > product.price && (
                      <span className="text-sm text-slate-400 line-through">
                        R$ {product.originalPrice.toFixed(2).replace('.', ',')}
                      </span>
                    )}
                  </div>
                </div>
                
                {/* Actions - sempre no fundo */}
                <div className="flex gap-3 mt-auto">
                  <Button
                    data-testid={`buy-btn-${product.id}`}
                    onClick={() => handleBuyClick(product)}
                    className={`flex-1 font-semibold py-3 rounded-lg flex items-center justify-center gap-2 transition-all duration-300 ${
                      addedProduct === product.id
                        ? 'bg-green-500 hover:bg-green-500 text-white'
                        : 'bg-amber-500 hover:bg-amber-400 text-slate-900'
                    }`}
                  >
                    {addedProduct === product.id ? (
                      <>
                        <CheckCircle className="w-4 h-4" />
                        Adicionado!
                      </>
                    ) : (
                      <>
                        <ShoppingCart className="w-4 h-4" />
                        Adicionar
                      </>
                    )}
                  </Button>
                  <Button
                    data-testid={`whatsapp-btn-${product.id}`}
                    onClick={() => handleWhatsAppClick(product)}
                    variant="outline"
                    className="px-4 border-slate-200 hover:bg-slate-50 hover:border-slate-300 rounded-lg transition-colors duration-200"
                  >
                    <MessageCircle className="w-5 h-5 text-amber-500" />
                  </Button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
        )}

        <motion.div variants={fadeInUp} className="text-center mt-12">
          <Link to="/produtos">
            <Button
              data-testid="view-all-products-btn"
              variant="outline"
              className="border-slate-300 text-slate-700 hover:bg-slate-50 hover:border-slate-400 font-semibold px-8 py-4 rounded-lg inline-flex items-center gap-2 transition-colors duration-200"
            >
              Ver Todos os Produtos
              <ArrowRight className="w-4 h-4" />
            </Button>
          </Link>
        </motion.div>
      </div>
    </AnimatedSection>
  );
};

// How It Works Section
const HowItWorksSection = () => {
  const steps = [
    {
      number: '01',
      title: 'Escolha',
      description: 'Navegue pelos nossos produtos ou serviços e escolha a solução ideal para seu projeto.',
    },
    {
      number: '02',
      title: 'Contato',
      description: 'Fale conosco pelo WhatsApp para tirar dúvidas ou compre diretamente pela loja virtual.',
    },
    {
      number: '03',
      title: 'Receba',
      description: 'Receba seus produtos em casa ou agende a execução do serviço com suporte técnico.',
    },
  ];

  return (
    <AnimatedSection className="py-20 md:py-32 bg-slate-900">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <motion.div variants={fadeInUp} className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-amber-400 text-xs font-bold uppercase tracking-[0.2em] mb-4 block">
            Simples e Rápido
          </span>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-extrabold text-white uppercase tracking-tight">
            Como Funciona
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8 md:gap-12 relative">
          {/* Connection line */}
          <div className="hidden md:block absolute top-20 left-[20%] right-[20%] h-px bg-slate-700"></div>
          
          {steps.map((step, index) => (
            <motion.div
              key={step.number}
              variants={fadeInUp}
              data-testid={`step-${index + 1}`}
              className="text-center relative"
            >
              <div className="w-16 h-16 bg-amber-500 rounded-xl flex items-center justify-center mx-auto mb-6 relative z-10">
                <span className="font-heading text-2xl font-bold text-slate-900">{step.number}</span>
              </div>
              <h3 className="font-heading text-2xl font-bold text-white uppercase mb-3">
                {step.title}
              </h3>
              <p className="text-slate-400 leading-relaxed max-w-xs mx-auto">
                {step.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </AnimatedSection>
  );
};

// About Section
const AboutSection = () => {
  return (
    <AnimatedSection id="sobre" className="py-20 md:py-32 bg-white">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <motion.div variants={fadeInUp}>
            <span className="text-amber-600 text-xs font-bold uppercase tracking-[0.2em] mb-4 block">
              Sobre Nós
            </span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-extrabold text-slate-900 uppercase tracking-tight mb-6 leading-tight">
              Mais de Duas Décadas<br />
              <span className="text-slate-500">de Excelência</span>
            </h2>
            <div className="industrial-border">
              <p className="text-slate-600 text-base md:text-lg leading-relaxed mb-6">
                A Ematech nasceu da prática, com raízes na construção civil desde os anos 80 e um histórico de atuação em obras por todo o Brasil. Unimos conhecimento químico, aplicação técnica e fabricação própria de produtos.
              </p>
              <p className="text-slate-600 text-base md:text-lg leading-relaxed">
                Hoje, atuamos com foco em serviço técnico de qualidade, atendendo desde pequenas intervenções até grandes estruturas como galpões, shoppings, pontes e viadutos.
              </p>
            </div>
            
            <div className="mt-8 grid grid-cols-2 gap-6">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-6 h-6 text-amber-500" />
                <span className="text-slate-700 font-medium">Fabricação Própria</span>
              </div>
              <div className="flex items-center gap-3">
                <CheckCircle className="w-6 h-6 text-amber-500" />
                <span className="text-slate-700 font-medium">Equipe Técnica</span>
              </div>
              <div className="flex items-center gap-3">
                <CheckCircle className="w-6 h-6 text-amber-500" />
                <span className="text-slate-700 font-medium">Preço Competitivo</span>
              </div>
              <div className="flex items-center gap-3">
                <CheckCircle className="w-6 h-6 text-amber-500" />
                <span className="text-slate-700 font-medium">Suporte Completo</span>
              </div>
            </div>
            
            {/* CTA para página Conheça */}
            <div className="mt-10">
              <Link to="/conheca">
                <Button
                  data-testid="about-gallery-btn"
                  className="bg-slate-900 hover:bg-slate-800 text-white font-semibold px-6 py-3 rounded-lg flex items-center gap-2 transition-colors duration-200"
                >
                  Ver Vídeos e Galeria de Projetos
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>
          </motion.div>
          
          <motion.div variants={fadeInUp} className="relative">
            <div className="aspect-[4/3] rounded-xl overflow-hidden shadow-2xl">
              <img
                src="/images/industrial-worker.jpg"
                alt="Equipe Ematech"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-amber-500 text-slate-900 p-6 rounded-lg shadow-xl">
              <div className="font-heading text-4xl font-bold">+20</div>
              <div className="text-green-100 text-sm">Anos de Mercado</div>
            </div>
          </motion.div>
        </div>
      </div>
    </AnimatedSection>
  );
};

// Testimonials Section
const TestimonialsSection = () => {
  const testimonials = [
    {
      name: 'Ricardo S.',
      role: 'Engenheiro Civil - São Paulo/SP',
      content: 'A Ematech resolveu um problema estrutural antigo no nosso prédio em menos de uma semana. Equipe extremamente profissional e rápida.',
      rating: 5,
      image: '/images/testimonials/pessoa1.jpg',
    },
    {
      name: 'Paula F.',
      role: 'Gerente de Operações - Joinville/SC',
      content: 'Revestimos mais de 1.000 m² de piso industrial com tinta epóxi da Ematech. Serviço limpo, durável e com suporte técnico excelente.',
      rating: 5,
      image: '/images/testimonials/pessoa2.jpg',
    },
    {
      name: 'Marcos A.',
      role: 'Artesão - Belo Horizonte/MG',
      content: 'As resinas da Ematech têm qualidade superior. Uso para mesas de resina e o acabamento é perfeito. Recomendo!',
      rating: 5,
      image: '/images/testimonials/pessoa3.jpg',
    },
  ];

  return (
    <AnimatedSection className="py-20 md:py-32 bg-slate-50">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <motion.div variants={fadeInUp} className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-amber-600 text-xs font-bold uppercase tracking-[0.2em] mb-4 block">
            Depoimentos
          </span>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-extrabold text-slate-900 uppercase tracking-tight leading-tight">
            O que Nossos<br />
            <span className="text-slate-500">Clientes Dizem</span>
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={index}
              variants={fadeInUp}
              data-testid={`testimonial-${index}`}
              className="bg-white p-7 rounded-xl border border-slate-200/60 shadow-sm hover:shadow-md transition-shadow duration-200"
            >
              <div className="flex gap-1 mb-4">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 text-amber-400 fill-amber-400" />
                ))}
              </div>
              <p className="text-slate-600 leading-relaxed mb-6">
                "{testimonial.content}"
              </p>
              <div className="flex items-center gap-4 pt-4 border-t border-slate-100">
                <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0">
                  <img 
                    src={testimonial.image} 
                    alt={testimonial.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <div className="font-semibold text-slate-900">{testimonial.name}</div>
                  <div className="text-sm text-slate-500">{testimonial.role}</div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </AnimatedSection>
  );
};

// FAQ Item Component
const FAQItem = ({ question, answer, isOpen, onClick }) => {
  return (
    <motion.div 
      className="border-b border-slate-200 last:border-b-0"
      initial={false}
    >
      <button
        onClick={onClick}
        className="w-full py-5 flex items-center justify-between text-left hover:bg-slate-50 px-4 -mx-4 rounded-lg transition-colors"
        data-testid={`faq-question-${question.slice(0, 20).replace(/\s/g, '-').toLowerCase()}`}
      >
        <span className="font-semibold text-slate-900 pr-8">{question}</span>
        <ChevronDown 
          className={`w-5 h-5 text-amber-500 flex-shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} 
        />
      </button>
      <motion.div
        initial={false}
        animate={{ height: isOpen ? 'auto' : 0, opacity: isOpen ? 1 : 0 }}
        transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
        className="overflow-hidden"
      >
        <p className="pb-5 text-slate-600 leading-relaxed">{answer}</p>
      </motion.div>
    </motion.div>
  );
};

// FAQ Section
const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const faqs = [
    {
      question: 'Vocês atuam em obras de pequeno porte?',
      answer: 'Sim. Embora atendamos grandes projetos, também executamos serviços localizados com a mesma precisão e qualidade.',
    },
    {
      question: 'Qual é o prazo médio de execução?',
      answer: 'Depende da metragem e da complexidade do projeto. Nosso diferencial é a qualidade e agilidade com que entregamos cada serviço. Entre em contato para um orçamento personalizado.',
    },
    {
      question: 'Posso comprar apenas os produtos?',
      answer: 'Sim, temos um portfólio completo de produtos voltados para aplicadores e profissionais da área. Acesse nossa loja virtual ou entre em contato pelo WhatsApp.',
    },
    {
      question: 'Vocês atuam em quais estados?',
      answer: 'A Ematech atua em todo o Brasil. O orçamento pode variar de região para região de acordo com os custos de deslocamento e execução.',
    },
    {
      question: 'Quais tipos de pisos vocês revestem?',
      answer: 'Revestimos pisos industriais, comerciais e de alto tráfego — como galpões, garagens, shoppings, cozinhas industriais e frigoríficos — com tintas PU e epóxi de alta resistência.',
    },
    {
      question: 'Vocês trabalham com fornecimento e aplicação de epóxi injetável?',
      answer: 'Sim, realizamos a aplicação técnica de epóxi injetável para reforço estrutural, vedação de fissuras e união de concretos, especialmente em pilares, vigas e fundações.',
    },
    {
      question: 'Os serviços seguem normas técnicas?',
      answer: 'Sim. Toda execução segue as exigências da engenharia civil e normas técnicas brasileiras (como NBR), com foco em durabilidade e segurança.',
    },
    {
      question: 'Qual a diferença entre o serviço de vocês e de empresas que só aplicam pintura?',
      answer: 'Além da aplicação, entregamos análise técnica, preparo de base, uso de insumos próprios e acompanhamento especializado, o que garante um resultado mais seguro e duradouro — sem retrabalho.',
    },
  ];

  // Filtrar FAQs com base na busca
  const filteredFaqs = faqs.filter(faq => 
    faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
    faq.answer.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <AnimatedSection id="faq" className="py-20 md:py-32 bg-white">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Left Side - Title */}
          <motion.div variants={fadeInUp}>
            <span className="text-amber-600 text-xs font-bold uppercase tracking-[0.2em] mb-4 block">
              Tire suas dúvidas
            </span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-extrabold text-slate-900 uppercase tracking-tight mb-6 leading-tight">
              Perguntas<br />
              <span className="text-slate-500">Frequentes</span>
            </h2>
            <p className="text-slate-600 text-base md:text-lg leading-relaxed mb-8">
              Encontre respostas para as principais dúvidas sobre nossos serviços e produtos. Não encontrou o que procura? Entre em contato conosco!
            </p>
            
            <div className="flex items-center gap-4 p-4 bg-amber-50 rounded-xl border border-amber-200">
              <div className="w-12 h-12 bg-amber-500 rounded-lg flex items-center justify-center flex-shrink-0">
                <HelpCircle className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="font-semibold text-slate-900 text-sm">Ainda tem dúvidas?</p>
                <p className="text-slate-600 text-sm">Fale conosco pelo WhatsApp</p>
              </div>
            </div>
          </motion.div>

          {/* Right Side - FAQ Items */}
          <motion.div variants={fadeInUp} className="bg-slate-50 rounded-2xl p-6 md:p-8">
            {/* Search Input */}
            <div className="relative mb-6">
              <input
                type="text"
                placeholder="Buscar pergunta..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-3 pl-11 bg-white border border-slate-200 rounded-lg text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                data-testid="faq-search-input"
              />
              <svg 
                className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            
            {/* FAQ Items */}
            {filteredFaqs.length > 0 ? (
              filteredFaqs.map((faq, index) => (
                <FAQItem
                  key={index}
                  question={faq.question}
                  answer={faq.answer}
                  isOpen={openIndex === index}
                  onClick={() => toggleFAQ(index)}
                />
              ))
            ) : (
              <div className="text-center py-8 text-slate-500">
                <p>Nenhuma pergunta encontrada.</p>
                <p className="text-sm mt-2">Tente buscar por outras palavras ou entre em contato conosco.</p>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </AnimatedSection>
  );
};

// Final CTA Section
const FinalCTASection = () => {
  const handleWhatsAppClick = () => {
    if (window.dataLayer) {
      window.dataLayer.push({ event: 'whatsapp_click', event_label: 'final_cta' });
    }
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(WHATSAPP_MESSAGE)}`, '_blank');
  };

  return (
    <AnimatedSection className="py-20 md:py-32 bg-slate-900">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <motion.div variants={fadeInUp} className="text-center max-w-3xl mx-auto">
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-extrabold text-white uppercase tracking-tight mb-6 leading-tight">
            Pronto para Começar<br />
            <span className="text-amber-400">seu Projeto?</span>
          </h2>
          <p className="text-slate-400 text-base md:text-lg mb-10 max-w-xl mx-auto leading-relaxed">
            Entre em contato agora e receba um orçamento personalizado em até 24h. Atendemos todo o Brasil.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              data-testid="final-cta-whatsapp"
              onClick={handleWhatsAppClick}
              className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold px-10 py-6 text-lg rounded-lg shadow-lg hover:shadow-xl flex items-center justify-center gap-3 uppercase tracking-widest transition-all duration-200 hover:-translate-y-0.5"
            >
              <Phone className="w-5 h-5" />
              Falar no WhatsApp
            </Button>
            
            <Link to="/produtos">
              <Button
                data-testid="final-cta-products"
                variant="outline"
                className="w-full sm:w-auto bg-transparent border-white/25 text-white hover:bg-white/10 hover:border-white/40 font-semibold px-10 py-6 text-lg rounded-lg flex items-center justify-center gap-3 transition-all duration-200"
              >
                <ShoppingCart className="w-5 h-5" />
                Ver Produtos
              </Button>
            </Link>
          </div>
        </motion.div>
      </div>
    </AnimatedSection>
  );
};

// Main Home Component
export const Home = () => {
  const { addToCart } = useCart();
  const { products, loading } = useProducts();

  useEffect(() => {
    // Initialize Google Tag Manager dataLayer
    window.dataLayer = window.dataLayer || [];
    
    // Verifica se há um hash na URL e faz scroll para a seção
    const hash = window.location.hash.replace('#', '');
    if (hash) {
      // Aguarda o DOM carregar completamente
      setTimeout(() => {
        const element = document.getElementById(hash);
        if (element) {
          const headerOffset = 80;
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
          
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }, 100);
    }
  }, []);

  return (
    <main data-testid="home-page">
      <HeroSection />
      <AuthoritySection />
      <ServicesSection />
      <ProductsSection addToCart={addToCart} products={products} loading={loading} />
      <HowItWorksSection />
      <AboutSection />
      <TestimonialsSection />
      <FAQSection />
      <FinalCTASection />
    </main>
  );
};

export default Home;
